"use client";

import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";

const STATS = [
  {
    id: "experience",
    number: "20",
    label: "شبکه نمایندگی های برساوش",
    highlight: false,
  },
  {
    id: "satisfaction",
    number: "13",
    label: "تعداد محصولات",
    highlight: false,
  },
  {
    id: "support",
    number: "24/7",
    label: "پشتیبانی فعال",
    highlight: false,
  },
];

export default function TrustBanner() {
  const sectionRef = useRef(null);

  // textProgress goes from 0 (bottom, visible) to 1 (top, invisible)
  const [textProgress, setTextProgress] = useState(0);
  const progressRef = useRef(0);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    let startY = 0;
    let isTouching = false;

    // Wheel event handler for desktop mouse/trackpad scrolling
    const handleWheel = (e) => {
      const rect = section.getBoundingClientRect();

      // Magnetic catch zone: intercept scroll when section is mostly visible
      const vh = window.innerHeight;
      if (rect.top > -vh * 0.75 && rect.top <= vh * 0.75) {
        const scrollingDown = e.deltaY > 0;
        const scrollingUp = e.deltaY < 0;

        // Only lock and animate if there is unfinished animation in the scroll direction
        if ((scrollingDown && progressRef.current < 1) || (scrollingUp && progressRef.current > 0)) {
          e.preventDefault(); // Stop native page scroll

          // Snap perfectly to the section top to ensure 100vh lock
          if (Math.abs(rect.top) >= 1) {
            window.scrollBy(0, rect.top);
          }

          // Progress the animation based on wheel delta
          const step = Math.abs(e.deltaY) * 0.0015;
          const dir = scrollingDown ? 1 : -1;
          const newProg = Math.max(0, Math.min(1, progressRef.current + (dir * step)));

          progressRef.current = newProg;
          setTextProgress(newProg);
        }
      }
    };

    // Touch handlers for mobile swiping
    const handleTouchStart = (e) => {
      startY = e.touches[0].clientY;
      isTouching = true;
    };

    const handleTouchMove = (e) => {
      if (!isTouching) return;
      const rect = section.getBoundingClientRect();

      const vh = window.innerHeight;
      if (rect.top > -vh * 0.75 && rect.top <= vh * 0.75) {
        const currentY = e.touches[0].clientY;
        const deltaY = startY - currentY; // positive = swiping UP = scrolling DOWN

        const scrollingDown = deltaY > 0;
        const scrollingUp = deltaY < 0;

        if ((scrollingDown && progressRef.current < 1) || (scrollingUp && progressRef.current > 0)) {
          e.preventDefault();

          if (Math.abs(rect.top) >= 1) {
            window.scrollBy(0, rect.top);
          }

          // Apply movement multiplier for natural feel
          const step = Math.abs(deltaY) * 0.002;
          const dir = scrollingDown ? 1 : -1;
          const newProg = Math.max(0, Math.min(1, progressRef.current + (dir * step)));

          progressRef.current = newProg;
          setTextProgress(newProg);

          startY = currentY; // Reset for continuous smooth swiping
        }
      }
    };

    const handleTouchEnd = () => {
      isTouching = false;
    };

    // passive: false is REQUIRED to allow e.preventDefault() on scroll/touch events
    section.addEventListener('wheel', handleWheel, { passive: false });
    section.addEventListener('touchstart', handleTouchStart, { passive: false });
    section.addEventListener('touchmove', handleTouchMove, { passive: false });
    section.addEventListener('touchend', handleTouchEnd);

    // Reset progress automatically whenever the banner completely leaves the viewport
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) {
        progressRef.current = 0;
        setTextProgress(0);
      }
    }, { threshold: 0 });

    observer.observe(section);

    return () => {
      section.removeEventListener('wheel', handleWheel);
      section.removeEventListener('touchstart', handleTouchStart);
      section.removeEventListener('touchmove', handleTouchMove);
      section.removeEventListener('touchend', handleTouchEnd);
      observer.disconnect();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full h-screen bg-black select-none m-0 p-0 overflow-hidden"
    >
      {/* 1. Fullscreen Background Video */}
      <video
        src="/StarlightBarsavosh01.mp4"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* 2. Dark Overlay */}
      <div
        className="absolute inset-0 z-10 backdrop-blur-[0.5px]"
        style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.1) 30%, rgba(0,0,0,0.1) 65%, rgba(0,0,0,0.85) 100%)" }}
      />

      {/* 3. Invisible Gesture Overlay: Guarantees touch/scroll events are caught across the ENTIRE width and not absorbed by the video element */}
      <div className="absolute inset-0 w-full h-full z-30" />

      {/* 4. Text Overlay: Framer Motion animates automatically as textProgress state changes */}
      <motion.div
        animate={{
          y: `${-130 * textProgress}vh`,
          opacity: Math.max(1 - textProgress * 2, 0)
        }}
        transition={{ type: "spring", stiffness: 200, damping: 25, mass: 0.5 }}
        className="absolute inset-x-0 bottom-0 z-20 flex flex-col items-center justify-end px-4 sm:px-6 md:px-12 lg:px-16 pb-8 sm:pb-10 md:pb-16 pointer-events-none"
      >
        <div className="max-w-[1600px] w-full mx-auto flex flex-col items-center justify-center">

          {/* Poetic Centered Persian Quote in Leila Font */}
          <div className="max-w-3xl w-full text-center px-2 sm:px-4" dir="rtl">
            <p className="font-leila text-[25px] md:text-[28px] lg:text-[30px] text-white/95 leading-relaxed sm:leading-relaxed md:leading-[1.85] tracking-wide drop-shadow-2xl">
              هر حرکت، ردی از خود به جا می‌گذارد. ما باور داریم بهترین حرکت‌ها، آن‌هایی هستند که مسیر آینده را روشن می‌کنند. برساوش با همین نگاه شکل گرفت؛ برای انتخاب‌هایی که فراتر از امروز باشند تا تجربه‌ای هوشمند، ماندگار و قابل اعتماد از حرکت بسازند.
            </p>
          </div>

          {/* Horizontal Light Divider Line */}
          <div className="w-full max-w-[1500px] h-[2px] bg-white my-5 sm:my-6 md:my-10 " />

          {/* Dynamic Statistics Counter Grid */}
          <div className="flex flex-wrap justify-center w-full max-w-[1500px] gap-8 sm:gap-6 md:gap-12" dir="rtl">
            {STATS.map((stat) => (
              <div key={stat.id} className="flex flex-col items-center text-center gap-1 flex-1 min-w-[140px] md:min-w-[200px] max-w-[350px]">
                <span
                  className="text-[30px] md:text-[40px] lg:text-[50px] font-anjoman-thin tracking-tight text-white drop-shadow-md"
                >
                  {stat.number}
                </span>
                <span className="text-[20px] sm:text-sm md:text-base font-anjoman-regular text-gray-200">
                  {stat.label}
                </span>
              </div>
            ))}
          </div>

        </div>
      </motion.div>
    </section>
  );
}
